// Mock seal search over an invented DEMO- fixture. Mirrors the Pimcore seal-search API
// (b2b-portal-seal-lookup.md sections 5–6) so the real adapter can replace it without UI changes.

export const BLANK_TOL_MM = 0.02;
export const MISSING_PENALTY = 1000;
const ROLES = ['inner', 'outer', 'height'];
const PAGE_SIZE = 50;
const RANK_LIMIT = 1000;
const factor = (u) => (u === 'in' ? 25.4 : 1);

export class ApiError extends Error {
  constructor(status, body) {
    super(`HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

// --- Fixture ---------------------------------------------------------------

const L = (inner, outer, height) => ({ inner, outer, height });
const GENERIC_LABELS = {
  seal: L('Seal ID d', 'Seal OD D', 'Seal height H'),
  housing_any: L('Inner Ø', 'Outer Ø', 'Length'),
  housing_rod: L('Rod Ø d', 'Groove Ø D1', 'Groove length H1'),
  housing_piston: L('Groove Ø d1', 'Bore Ø D', 'Groove length H1'),
};

const ORING = { seal: L('ID d', 'OD D', 'Cross-section W') };
const PISTON = { housing_any: L('Groove Ø d1', 'Bore Ø D', 'Groove length H1') };
const ROD = { housing_any: L('Rod Ø d', 'Groove Ø D1', 'Groove length H1') };
const WIPER = { housing_any: L('Rod Ø d', 'Groove Ø D1', 'Groove width h1'), housing_rod: L('Rod Ø d', 'Groove Ø D1', 'Groove width h1') };
const BACKUP = { housing_any: L('Rod Ø d', 'Bore Ø D', 'Length') };
const img = (file) => encodeURI(`/seal-types/${file}`);

// [family, family slug, [[type slug, name, application, label overrides, image]]], in display order:
// families and Hydraulic Seals types as on the MHS seal search, other families' types by name.
const FAMILIES = [
  ['Hydraulic Seals', 'hydraulic-seals', [
    ['vee-packs', 'Vee Packs', 'other', { housing_any: L('Rod Ø d', 'Bore Ø D', 'Stack length') }],
    ['single-acting-symmetrical-seals', 'S/A Symmetrical', 'other', { housing_piston: L('Groove Ø d2', 'Bore Ø D2', 'Groove length H1') }],
    ['rod-seals', 'Rod Seals', 'rod', ROD, img('Rod Seals.png')],
    ['seal-back-up', 'Seal Back-Up', 'other', BACKUP],
    ['double-acting-piston-seals', 'D/A Piston Seals', 'piston', PISTON, img('Double Acting Piston Seals.png')],
    ['wipers', 'Wipers', 'rod', WIPER, img('Flexible Wipers.png')],
    ['wear-strip', 'Wear Strip', 'other'],
    ['guide-rings', 'Guide Rings', 'other', { housing_any: L('Rod / groove Ø d', 'Bore / groove Ø D', 'Width') }, img('Guide Rings.png')],
    ['single-acting-piston-seals', 'S/A Piston Seals', 'piston', PISTON, img('Single Acting Piston Seals.png')],
    ['rod-buffer-seals', 'Rod Buffer Seals', 'rod', ROD],
    ['machine-seal-material', 'Machine Seal Material', 'other'],
    ['double-acting-rod-seals', 'D/A Rod Seals', 'rod', ROD],
    ['wiper-cord', 'Wiper Cord', 'other'],
    ['retaining-rings', 'Retaining Rings', 'other', { housing_any: L('Shaft Ø', 'Bore Ø', 'Groove width') }],
    ['pneumatic-double-acting-piston-seals', 'Pneumatic D/A Piston Seals', 'piston', PISTON],
    ['unitised-pistons', 'Unitised Pistons', 'piston', PISTON],
  ]],
  ['Pneumatic Seals', 'pneumatic-seals', [['cushion-seals', 'Cushion Seals', 'other']]],
  ['Rotary Seals', 'rotary-seals', [['v-rings', 'V-Rings', 'other']]],
  ['O-Rings/Static Seals', 'o-rings-static-seals', [
    ['bonded-washers', 'Bonded Washers', 'other'],
    ['flange-seals', 'Flange Seals', 'other'],
    ['o-ring-back-up-rings', 'O-Ring Back-Up Rings', 'other', { seal: L('ID', 'OD', 'Thickness'), ...BACKUP }],
    ['o-ring-cord', 'O-Ring Cord', 'other', ORING],
    ['o-ring-kits', 'O-Ring Kits', 'other'],
    ['o-rings', 'O-Rings', 'other', ORING],
    ['quad-rings', 'Quad Rings', 'other', ORING],
    ['static-seals', 'Static Seals', 'other'],
  ]],
  ['Cylinder Kits & Parts', 'cylinder-kits-and-parts', [
    ['composite-bearing', 'Composite Bearing', 'other', { housing_any: L('Rod Ø d', 'Bore Ø D', 'Width') }],
    ['cylinder-components', 'Cylinder Components', 'other'],
    ['tools', 'Tools', 'other'],
  ]],
];

const SEAL_TYPES = FAMILIES.flatMap(([family, slug, types]) =>
  types.map(([typeSlug, name, application, overrides = {}, image = null]) => ({
    key: `${slug}-${typeSlug}`, name, family, image, application,
    labels: { ...GENERIC_LABELS, ...overrides },
  }))
);

const PROFILES = [
  { id: 9001, code: 'DEMO-RU', title: 'DEMO-RU - Rod seal, U-cup (metric)', type: 'hydraulic-seals-rod-seals', measurementSystem: 'metric' },
  { id: 9002, code: 'DEMO-RUI', title: 'DEMO-RUI - Rod seal, U-cup (inch)', type: 'hydraulic-seals-rod-seals', measurementSystem: 'imperial' },
  { id: 9003, code: 'DEMO-PD', title: 'DEMO-PD - Double acting piston seal', type: 'hydraulic-seals-double-acting-piston-seals', measurementSystem: 'metric' },
  { id: 9004, code: 'DEMO-SY', title: 'DEMO-SY - Symmetrical seal', type: 'hydraulic-seals-single-acting-symmetrical-seals', measurementSystem: 'metric' },
  { id: 9005, code: 'DEMO-OR', title: 'DEMO-OR - O-ring', type: 'o-rings-static-seals-o-rings', measurementSystem: 'metric' },
  { id: 9006, code: 'DEMO-VA', title: 'DEMO-VA - V-ring', type: 'rotary-seals-v-rings', measurementSystem: 'metric' },
  { id: 9007, code: 'DEMO-GR', title: 'DEMO-GR - Guide ring', type: 'hydraulic-seals-guide-rings', measurementSystem: 'metric' },
];

const RU_SEAL = { inner: 'sealDiaInner', outer: 'sealDiaOuter', height: 'sealHeightMajor' };
const RU_FIT = { application: 'rod', inner: 'rodDiameter', outer: 'housingDiaOuter1', height: 'housingHeightMajor1' };

const PARTS = [
  { code: 'DEMO-RU-050-060-08', profile: 9001, material: 'Polyurethane 93A', tempMinC: -35, tempMaxC: 110, pressureMaxBar: 400,
    dims: [
      { key: 'rodDiameter', title: 'Rod Ø d', value: 50, unit: 'mm', tolUpper: 0, tolLower: -0.05 },
      { key: 'sealDiaInner', title: 'Seal d (inner Ø)', value: 50, unit: 'mm' },
      { key: 'sealDiaOuter', title: 'Seal D (outer Ø)', value: 60, unit: 'mm' },
      { key: 'sealHeightMajor', title: 'Seal H', value: 8, unit: 'mm' },
      { key: 'housingDiaOuter1', title: 'Groove Ø D1', value: 60, unit: 'mm', tolUpper: 0.19, tolLower: 0 },
      { key: 'housingHeightMajor1', title: 'Groove length H1', value: 8.5, unit: 'mm', tolUpper: 0.2, tolLower: 0 }],
    seal: RU_SEAL, fits: [RU_FIT] },

  { code: 'DEMO-RU-050-058-07', profile: 9001, material: 'Nitrile NBR 85A', tempMinC: -30, tempMaxC: 100, pressureMaxBar: 250,
    dims: [
      { key: 'rodDiameter', title: 'Rod Ø d', value: 50, unit: 'mm', tolUpper: 0, tolLower: -0.05 },
      { key: 'sealDiaInner', title: 'Seal d (inner Ø)', value: 50, unit: 'mm' },
      { key: 'sealDiaOuter', title: 'Seal D (outer Ø)', value: 58, unit: 'mm' },
      { key: 'sealHeightMajor', title: 'Seal H', value: 7, unit: 'mm' },
      { key: 'housingDiaOuter1', title: 'Groove Ø D1', value: 58, unit: 'mm', tolUpper: 0.19, tolLower: 0 },
      { key: 'housingHeightMajor1', title: 'Groove length H1', value: 7.5, unit: 'mm', tolUpper: 0.2, tolLower: 0 }],
    seal: RU_SEAL, fits: [RU_FIT] },

  { code: 'DEMO-RU-063-073-10', profile: 9001, material: 'Polyurethane 93A', tempMinC: -35, tempMaxC: 110, pressureMaxBar: 400,
    dims: [
      { key: 'rodDiameter', title: 'Rod Ø d', value: 63, unit: 'mm', tolUpper: 0, tolLower: -0.05 },
      { key: 'sealDiaOuter', title: 'Seal D (outer Ø)', value: 73, unit: 'mm' },
      { key: 'sealHeightMajor', title: 'Seal H', value: 10, unit: 'mm' },
      { key: 'housingDiaOuter1', title: 'Groove Ø D1', value: 73, unit: 'mm', tolUpper: 0.19, tolLower: 0 },
      { key: 'housingHeightMajor1', title: 'Groove length H1', value: 11, unit: 'mm', tolUpper: 0.2, tolLower: 0 }],
    seal: { ...RU_SEAL, inner: '~rodDiameter' }, fits: [RU_FIT] },

  { code: 'DEMO-RUI-0875-1250-0250', profile: 9002, material: 'Polyurethane 93A', tempMinC: -35, tempMaxC: 110, pressureMaxBar: 345,
    dims: [
      { key: 'rodDiameter', title: 'Rod Ø d', value: 0.875, unit: 'in', tolUpper: 0, tolLower: -0.002 },
      { key: 'sealDiaInner', title: 'Seal d (inner Ø)', value: 0.875, unit: 'in' },
      { key: 'sealDiaOuter', title: 'Seal D (outer Ø)', value: 1.25, unit: 'in' },
      { key: 'sealHeightMajor', title: 'Seal H', value: 0.25, unit: 'in' },
      { key: 'housingDiaOuter1', title: 'Groove Ø D1', value: 1.25, unit: 'in', tolUpper: 0.004, tolLower: 0 },
      { key: 'housingHeightMajor1', title: 'Groove length H1', value: 0.281, unit: 'in', tolUpper: 0.008, tolLower: 0 }],
    seal: RU_SEAL, fits: [RU_FIT] },

  { code: 'DEMO-PD-080-064-20', profile: 9003, material: 'Filled PTFE / NBR', tempMinC: -30, tempMaxC: 120, pressureMaxBar: 400,
    dims: [
      { key: 'boreDiameter', title: 'Bore Ø D', value: 80, unit: 'mm', tolUpper: 0.046, tolLower: 0 },
      { key: 'grooveDiaInner1', title: 'Groove Ø d1', value: 64, unit: 'mm', tolUpper: 0, tolLower: -0.074 },
      { key: 'grooveLength1', title: 'Groove length H1', value: 20.5, unit: 'mm', tolUpper: 0.2, tolLower: 0 },
      { key: 'sealDiaInner', title: 'Seal d (inner Ø)', value: 64, unit: 'mm' },
      { key: 'sealDiaOuter', title: 'Seal D (outer Ø)', value: 80, unit: 'mm' },
      { key: 'sealHeightMajor', title: 'Seal H', value: 20, unit: 'mm' }],
    seal: RU_SEAL,
    fits: [{ application: 'piston', inner: 'grooveDiaInner1', outer: 'boreDiameter', height: 'grooveLength1' }] },

  { code: 'DEMO-SY-040-050-06', profile: 9004, material: 'Nitrile NBR 85A', tempMinC: -30, tempMaxC: 100, pressureMaxBar: 160,
    dims: [
      { key: 'rodDiameter', title: 'Rod Ø d', value: 40, unit: 'mm' },
      { key: 'housingDiaOuter1', title: 'Groove Ø D1', value: 50, unit: 'mm' },
      { key: 'grooveDiaInner2', title: 'Groove Ø d2', value: 40, unit: 'mm' },
      { key: 'boreDiameter2', title: 'Bore Ø D2', value: 50, unit: 'mm' },
      { key: 'grooveLength1', title: 'Groove length H1', value: 6.3, unit: 'mm', tolUpper: 0.2, tolLower: 0 },
      { key: 'sealDiaInner', title: 'Seal d (inner Ø)', value: 40, unit: 'mm' },
      { key: 'sealDiaOuter', title: 'Seal D (outer Ø)', value: 50, unit: 'mm' },
      { key: 'sealHeightMajor', title: 'Seal H', value: 6, unit: 'mm' }],
    seal: RU_SEAL,
    fits: [
      { application: 'rod', inner: 'rodDiameter', outer: 'housingDiaOuter1', height: 'grooveLength1' },
      { application: 'piston', inner: 'grooveDiaInner2', outer: 'boreDiameter2', height: 'grooveLength1' }] },

  { code: 'DEMO-OR-020.0x2.5', profile: 9005, material: 'Fluorocarbon FKM 75A', tempMinC: -20, tempMaxC: 200, pressureMaxBar: null,
    dims: [
      { key: 'sealID', title: 'ID d', value: 20, unit: 'mm', tolUpper: 0.22, tolLower: -0.22 },
      { key: 'sealW', title: 'Cross-section W', value: 2.5, unit: 'mm', tolUpper: 0.08, tolLower: -0.08 }],
    seal: { inner: 'sealID', height: 'sealW' }, fits: [] },

  { code: 'DEMO-VA-0003', profile: 9006, material: 'Nitrile NBR 70A', tempMinC: -40, tempMaxC: 100, pressureMaxBar: null,
    dims: [
      { key: 'shaftDiameter', title: 'Shaft Ø d1', min: 2.7, max: 3.5, unit: 'mm' },
      { key: 'sealWidth', title: 'Seal width A', value: 2.5, unit: 'mm' }],
    seal: { inner: 'shaftDiameter', height: 'sealWidth' },
    fits: [{ application: 'any', inner: 'shaftDiameter' }] },

  { code: 'DEMO-GR-050-055-15', profile: 9007, material: 'Polyester resin / fabric', tempMinC: -40, tempMaxC: 120, pressureMaxBar: null,
    dims: [
      { key: 'rodDiameter', title: 'Rod / groove Ø d', value: 50, unit: 'mm' },
      { key: 'grooveDiaOuter', title: 'Groove Ø D', value: 55, unit: 'mm' },
      { key: 'ringWidth', title: 'Width', value: 15, unit: 'mm', tolUpper: 0, tolLower: -0.2 },
      { key: 'ringThickness', title: 'Thickness', value: 2.5, unit: 'mm' }],
    seal: { inner: 'rodDiameter', outer: 'grooveDiaOuter', height: 'ringWidth' },
    fits: [{ application: 'any', inner: 'rodDiameter', outer: 'grooveDiaOuter', height: 'ringWidth' }] },
];

const byCode = (a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0);
const typeByKey = Object.fromEntries(SEAL_TYPES.map((t) => [t.key, t]));
const profileById = Object.fromEntries(PROFILES.map((p) => [p.id, p]));

const META = {
  sealTypes: SEAL_TYPES,
  profiles: PROFILES.map((p) => {
    const lengthKeys = [];
    PARTS.filter((part) => part.profile === p.id).forEach((part) =>
      part.dims.forEach((d) => {
        if (d.unit && !lengthKeys.some((k) => k.key === d.key)) lengthKeys.push({ key: d.key, title: d.title });
      })
    );
    return { ...p, lengthKeys };
  }).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' })),
  genericLabels: GENERIC_LABELS,
  blankToleranceMm: BLANK_TOL_MM,
};

// --- Reference helpers (spec 6.6) -------------------------------------------

/** "22.5", "22,5", ".5", "7/8", "1-1/2", "1 1/2" -> number; anything else -> null */
export function parseNumber(raw) {
  const s = raw.trim().replace(/,/g, '.');
  if (/^\d+(\.\d+)?$|^\.\d+$/.test(s)) return parseFloat(s);
  const m = s.match(/^(?:(\d+)[ -])?(\d+)\/(\d+)$/);
  return m && +m[3] > 0 ? +(m[1] ?? 0) + +m[2] / +m[3] : null;
}

/** A typed size and tolerance -> { target, tol } in mm, { error, onTol }, or null when blank. */
export function toWindow(raw, rawTol, units) {
  if (raw.trim() === '') return null;
  const v = parseNumber(raw);
  if (v === null) return { error: 'not a number', onTol: false };
  if (v <= 0) return { error: 'must be greater than 0', onTol: false };
  const target = v * factor(units);
  const t = rawTol.trim();
  if (t === '') return { target, tol: BLANK_TOL_MM };
  const pct = t.endsWith('%');
  const n = parseNumber(pct ? t.slice(0, -1) : t);
  if (n === null || (pct && n > 100)) return { error: pct ? 'percentage must be 0–100' : 'not a number', onTol: true };
  return { target, tol: Math.max(pct ? (target * n) / 100 : n * factor(units), 0.0001) };
}

function mmRange(d) {
  const f = factor(d.unit);
  if (d.min != null && d.max != null) return [d.min * f, d.max * f];
  return typeof d.value === 'number' ? [d.value * f, d.value * f] : null;
}

const overlaps = ([lo, hi], w) => hi >= w.target - w.tol - 1e-9 && lo <= w.target + w.tol + 1e-9;
const distanceTo = ([lo, hi], t) => Math.max(lo - t, 0, t - hi);

function fitDistance(wanted, have) {
  let score = 0;
  for (const [key, w] of Object.entries(wanted)) {
    const r = have[key];
    score += r ? distanceTo(r, w.target) / Math.max(w.tol, BLANK_TOL_MM) : MISSING_PENALTY;
  }
  return score;
}

// --- Search -----------------------------------------------------------------

const ENUMS = { mode: ['seal', 'housing'], app: ['any', 'rod', 'piston'], units: ['mm', 'in'], system: ['both', 'metric', 'imperial'] };

function parseParams(params) {
  const errors = {};
  const q = {};
  for (const [name, allowed] of Object.entries(ENUMS)) {
    const v = params.get(name) ?? allowed[0];
    if (!allowed.includes(v)) errors[name] = `must be one of ${allowed.join(', ')}`;
    q[name] = v;
  }
  q.q = (params.get('q') ?? '').trim();
  if (q.q.length > 200) errors.q = 'at most 200 characters';

  const window = (field, tolField, raw, rawTol) => {
    const w = toWindow(raw, rawTol ?? '', q.units);
    if (w && w.error) errors[w.onTol ? tolField : field] = w.error;
    return w && !w.error ? w : null;
  };
  q.sizes = {};
  for (const role of ROLES) {
    const w = window(role, `${role}Tol`, params.get(role) ?? '', params.get(`${role}Tol`));
    if (w) q.sizes[role] = w;
  }
  q.dims = {};
  for (const [name, raw] of params.entries()) {
    const key = name.match(/^dim\[(.*)\]$/)?.[1];
    if (key === undefined) continue;
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(key)) { errors[`dim.${key}`] = 'unknown dimension'; continue; }
    const w = window(`dim.${key}`, `dimTol.${key}`, raw, params.get(`dimTol[${key}]`));
    if (w) q.dims[key] = w;
  }

  q.types = params.getAll('types[]');
  if (q.types.some((t) => !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(t))) errors.types = 'invalid value';
  const profiles = params.getAll('profiles[]');
  if (profiles.some((p) => !/^\d+$/.test(p))) errors.profiles = 'invalid value';
  q.profiles = profiles.map(Number);

  const page = params.get('page') ?? '1';
  if (!/^\d+$/.test(page) || +page < 1) errors.page = 'must be a whole number from 1';
  q.page = +page;
  q.includeIncomplete = params.get('includeIncomplete') === '1';

  if (Object.keys(errors).length) throw new ApiError(400, { errors });
  return q;
}

const findDim = (part, ref) => (ref ? part.dims.find((d) => d.key === ref.replace(/^~/, '')) : undefined);

/** Role -> { ref, dim, range } for the roles a set (seal or fit) has. */
function roleSet(part, refs) {
  const out = {};
  for (const role of ROLES) {
    const dim = findDim(part, refs[role]);
    const range = dim && mmRange(dim);
    if (range) out[role] = { ref: refs[role], dim, range };
  }
  return out;
}

/** Sizes match a role set: strict = every size present and overlapping; incomplete = missing allowed, one present. */
function setMatches(set, sizes, incomplete) {
  const entered = Object.keys(sizes);
  if (!entered.length) return true;
  const present = entered.filter((r) => set[r]);
  if (!present.every((r) => overlaps(set[r].range, sizes[r]))) return false;
  return incomplete ? present.length > 0 : present.length === entered.length;
}

function candidateSets(part, q) {
  if (q.mode === 'seal') return [roleSet(part, part.seal)];
  return part.fits
    .filter((f) => q.app === 'any' || f.application === q.app || f.application === 'any')
    .map((f) => roleSet(part, f));
}

function dimsMatch(part, dims) {
  return Object.entries(dims).every(([key, w]) => {
    const d = part.dims.find((x) => x.key === key);
    const r = d && mmRange(d);
    return r && overlaps(r, w);
  });
}

const sizeFilter = (part, q, incomplete) =>
  dimsMatch(part, q.dims) &&
  (!Object.keys(q.sizes).length || candidateSets(part, q).some((set) => setMatches(set, q.sizes, incomplete)));

function moreDimensions(types, profiles) {
  const source = profiles.length
    ? META.profiles.filter((p) => profiles.includes(p.id))
    : types.length === 1 ? META.profiles.filter((p) => p.type === types[0]) : [];
  const out = [];
  source.forEach((p) => p.lengthKeys.forEach((k) => { if (!out.some((o) => o.key === k.key)) out.push(k); }));
  return out;
}

function toHit(part, q, ranked) {
  const profile = profileById[part.profile];
  let matched = {};
  let fit = null;
  if (ranked) {
    const dimRanges = Object.fromEntries(part.dims.map((d) => [d.key, mmRange(d)]));
    const dimScore = fitDistance(q.dims, dimRanges);
    const sets = candidateSets(part, q);
    let best = sets[0] || {};
    fit = sets.length ? Infinity : fitDistance(q.sizes, {}) + dimScore;
    for (const set of sets) {
      const score = fitDistance(q.sizes, Object.fromEntries(Object.entries(set).map(([r, v]) => [r, v.range]))) + dimScore;
      if (score < fit) { fit = score; best = set; }
    }
    fit = Math.round(fit * 1000) / 1000;
    for (const role of Object.keys(q.sizes)) {
      const m = best[role];
      if (!m) continue;
      const { dim, range } = m;
      const single = dim.min == null || dim.max == null;
      matched[role] = {
        key: dim.key, title: dim.title,
        min: single ? dim.value : dim.min, max: single ? dim.value : dim.max, unit: dim.unit ?? null,
        mmMin: range[0], mmMax: range[1], approx: m.ref.startsWith('~'),
      };
    }
  }
  return {
    code: part.code,
    profile: { id: profile.id, code: profile.code, title: profile.title },
    sealType: typeByKey[profile.type]?.name ?? null,
    measurementSystem: profile.measurementSystem,
    material: part.material ?? null,
    tempMinC: part.tempMinC ?? null,
    tempMaxC: part.tempMaxC ?? null,
    pressureMaxBar: part.pressureMaxBar ?? null,
    matched,
    fitDistance: Object.keys(q.sizes).length || Object.keys(q.dims).length ? fit : null,
    dims: part.dims.map((d) => ({
      key: d.key, title: d.title, value: d.value ?? null, min: d.min ?? null, max: d.max ?? null,
      unit: d.unit ?? null, tolUpper: d.tolUpper ?? null, tolLower: d.tolLower ?? null,
    })),
  };
}

/** Synchronous search over the fixture; throws ApiError(400) on invalid params. */
export function searchFixture(params) {
  const q = parseParams(params);
  const typeOf = (part) => profileById[part.profile].type;
  const words = q.q.toLowerCase().split(/\s+/).filter(Boolean);
  const base = PARTS.filter((part) => {
    const profile = profileById[part.profile];
    if (q.system !== 'both' && profile.measurementSystem !== q.system) return false;
    if (!words.length) return true;
    const code = part.code.toLowerCase();
    if (code.includes(q.q.toLowerCase())) return true;
    const hay = [part.code, profile.code, profile.title, typeByKey[profile.type]?.name, part.material].join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const inSelection = (part) =>
    (!q.types.length || q.types.includes(typeOf(part))) && (!q.profiles.length || q.profiles.includes(part.profile));

  const matching = base.filter((part) => sizeFilter(part, q, q.includeIncomplete));
  const results = matching.filter(inSelection);

  const typeCounts = {};
  matching.forEach((part) => { typeCounts[typeOf(part)] = (typeCounts[typeOf(part)] || 0) + 1; });
  const profileCounts = {};
  matching.filter((part) => !q.types.length || q.types.includes(typeOf(part)))
    .forEach((part) => { profileCounts[part.profile] = (profileCounts[part.profile] || 0) + 1; });

  const leftOut = { total: 0, byRole: {} };
  const entered = Object.keys(q.sizes);
  if (entered.length && !q.includeIncomplete) {
    const scope = base.filter((part) => inSelection(part) && sizeFilter(part, q, true));
    leftOut.total = scope.length - results.length;
    for (const role of entered) {
      const n = scope.filter((part) => !candidateSets(part, q).some((set) => set[role])).length;
      if (n > 0) leftOut.byRole[role] = n;
    }
  }

  const ranked = entered.length > 0 || Object.keys(q.dims).length > 0;
  let ordered = [...results].sort(byCode);
  let truncated = false;
  if (ranked) {
    truncated = ordered.length > RANK_LIMIT;
    ordered = ordered.slice(0, RANK_LIMIT).map((part) => [part, toHit(part, q, true)])
      .sort(([a, ha], [b, hb]) => ha.fitDistance - hb.fitDistance || byCode(a, b));
  } else {
    const kw = q.q.toLowerCase();
    const group = (part) => (!kw ? 0 : part.code.toLowerCase() === kw ? 0 : part.code.toLowerCase().includes(kw) ? 1 : 2);
    ordered = ordered.sort((a, b) => group(a) - group(b) || byCode(a, b)).map((part) => [part, null]);
  }
  const hits = ordered
    .slice((q.page - 1) * PAGE_SIZE, q.page * PAGE_SIZE)
    .map(([part, hit]) => hit || toHit(part, q, false));

  const labelKey = q.mode === 'seal' ? 'seal' : `housing_${q.app}`;
  const oneType = q.types.length === 1 ? typeByKey[q.types[0]] : null;

  return {
    mhs: { total: results.length, truncated, page: q.page, pageSize: PAGE_SIZE, hits },
    facets: {
      sealTypes: SEAL_TYPES.map((t) => ({ key: t.key, count: typeCounts[t.key] || 0 })),
      profiles: META.profiles.map((p) => ({ id: p.id, count: profileCounts[p.id] || 0 })),
    },
    leftOut,
    labels: (oneType ? oneType.labels : GENERIC_LABELS)[labelKey],
    moreDimensions: moreDimensions(q.types, q.profiles),
  };
}

const delay = () => new Promise((resolve) => setTimeout(resolve, 300 + Math.random() * 300));

/** SealLookupApi over the fixture, with a real-ish response delay so busy states get exercised. */
export class MockSealLookupApi {
  async meta() {
    await delay();
    return META;
  }

  async search(params) {
    await delay();
    return searchFixture(params);
  }
}
